import { createHashRouter, Navigate } from 'react-router-dom';
import { RootLayout } from '@/components/layout/root-layout';
import { AuthLayout } from '@/components/layout/auth-layout';
import { ProtectedRoute } from '@/components/layout/protected-route';
import { AdminRoute } from '@/components/layout/admin-route';

// Lazy load feature pages for code splitting
import { lazy, Suspense } from 'react';
import { LoadingPage } from '@/components/feedback/loading-page';
import { ChunkErrorBoundary } from '@/components/feedback/chunk-error-boundary';
const RouteErrorPage = lazy(() => import('@/components/feedback/route-error-page'));

// Auth pages
const LoginPage = lazy(() => import('@/features/auth/pages/login-page'));
const SignupLandingPage = lazy(() => import('@/features/auth/pages/signup-landing-page'));
const SignupPage = lazy(() => import('@/features/auth/pages/signup-page'));
const JoinOrganizationPage = lazy(() => import('@/features/auth/pages/join-organization-page'));
const IndividualSignupPage = lazy(() => import('@/features/auth/pages/individual-signup-page'));
const JoinRequestSuccessPage = lazy(() => import('@/features/auth/pages/join-request-success-page'));
const SignupSuccessPage = lazy(() => import('@/features/auth/pages/signup-success-page'));
const ChangePasswordPage = lazy(() => import('@/features/auth/pages/change-password-page'));
const PrivacyPage = lazy(() => import('@/features/auth/pages/privacy-page'));
const PartnerAgreementPage = lazy(() => import('@/features/auth/pages/partner-agreement-page'));

// Partner pages
const DashboardPage = lazy(() => import('@/features/dashboard/pages/dashboard-page'));
const DealsPage = lazy(() => import('@/features/deals/pages/deals-page'));
const DealDetailPage = lazy(() => import('@/features/deals/pages/deal-detail-page'));
const DealFormPage = lazy(() => import('@/features/deals/pages/deal-form-page'));
const ProfilePage = lazy(() => import('@/features/profile/pages/profile-page'));
const MfaSetupPage = lazy(() => import('@/features/profile/pages/mfa-setup-page'));
const CommissionsPage = lazy(() => import('@/features/commissions/pages/commissions-page'));
const CommissionDetailPage = lazy(() => import('@/features/commissions/pages/commission-detail-page'));
const TeamPage = lazy(() => import('@/features/team/pages/team-page'));
const ResourcesPage = lazy(() => import('@/features/resources/pages/resources-page'));
const TiersPage = lazy(() => import('@/features/tiers/pages/tiers-page'));

// Admin pages
const AdminDashboardPage = lazy(() => import('@/features/admin/pages/admin-dashboard-page'));
const AdminDealsPage = lazy(() => import('@/features/admin/pages/admin-deals-page'));
const AdminDealDetailPage = lazy(() => import('@/features/admin/pages/admin-deal-detail-page'));
const AdminCommissionsPage = lazy(() => import('@/features/admin/pages/admin-commissions-page'));
const AdminOrganizationsPage = lazy(() => import('@/features/admin/pages/admin-organizations-page'));
const AdminOrganizationDetailPage = lazy(() => import('@/features/admin/pages/admin-organization-detail-page'));
const AdminUsersPage = lazy(() => import('@/features/admin/pages/admin-users-page'));
const AdminUserDetailPage = lazy(() => import('@/features/admin/pages/admin-user-detail-page'));
const AdminCustomersPage = lazy(() => import('@/features/admin/pages/admin-customers-page'));
const AdminCustomerDetailPage = lazy(() => import('@/features/admin/pages/admin-customer-detail-page'));
const AdminAuditLogPage = lazy(() => import('@/features/admin/pages/admin-audit-log-page'));
const AdminReportsPage = lazy(() => import('@/features/admin/pages/admin-reports-page'));
const AdminOfferingsPage = lazy(() => import('@/features/admin/pages/admin-offerings-page'));
const AdminOfferingDetailPage = lazy(() => import('@/features/admin/pages/admin-offering-detail-page'));
const AdminScopingQuestionsPage = lazy(() => import('@/features/admin/pages/admin-scoping-questions-page'));
const AdminSettingsPage = lazy(() => import('@/features/admin/pages/admin-settings-page'));
const AdminResourcesPage = lazy(() => import('@/features/admin/pages/admin-resources-page'));
const AdminResourceAnalyticsPage = lazy(() => import('@/features/admin/pages/admin-resource-analytics-page'));
const ZohoTestingPage = lazy(() => import('@/features/admin/pages/settings/zoho-testing-page'));
const QuickBooksTestingPage = lazy(() => import('@/features/admin/pages/settings/quickbooks-testing-page'));
const RabbitSignTestingPage = lazy(() => import('@/features/admin/pages/settings/rabbitsign-testing-page'));
const GmailTestingPage = lazy(() => import('@/features/admin/pages/settings/gmail-testing-page'));

// Wrapper for lazy loaded pages with chunk error handling
function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<LoadingPage />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}

export const router = createHashRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: (
      <Suspense fallback={<LoadingPage />}>
        <RouteErrorPage />
      </Suspense>
    ),
    children: [
      // Public auth routes
      {
        element: <AuthLayout />,
        children: [
          { index: true, element: <Navigate to="/login" replace /> },
          {
            path: 'login',
            element: (
              <LazyPage>
                <LoginPage />
              </LazyPage>
            ),
          },
          {
            path: 'signup',
            element: (
              <LazyPage>
                <SignupLandingPage />
              </LazyPage>
            ),
          },
          {
            path: 'signup/company',
            element: (
              <LazyPage>
                <SignupPage />
              </LazyPage>
            ),
          },
          {
            path: 'signup/join',
            element: (
              <LazyPage>
                <JoinOrganizationPage />
              </LazyPage>
            ),
          },
          {
            path: 'join-request-success',
            element: (
              <LazyPage>
                <JoinRequestSuccessPage />
              </LazyPage>
            ),
          },
          {
            path: 'signup/individual',
            element: (
              <LazyPage>
                <IndividualSignupPage />
              </LazyPage>
            ),
          },
          {
            path: 'signup-success',
            element: (
              <LazyPage>
                <SignupSuccessPage />
              </LazyPage>
            ),
          },
          {
            path: 'privacy',
            element: (
              <LazyPage>
                <PrivacyPage />
              </LazyPage>
            ),
          },
          {
            path: 'partner-agreement',
            element: (
              <LazyPage>
                <PartnerAgreementPage />
              </LazyPage>
            ),
          },
        ],
      },
      // Protected partner routes
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: 'dashboard',
            element: (
              <LazyPage>
                <DashboardPage />
              </LazyPage>
            ),
          },
          {
            path: 'change-password',
            element: (
              <LazyPage>
                <ChangePasswordPage />
              </LazyPage>
            ),
          },
          {
            path: 'deals',
            element: (
              <LazyPage>
                <DealsPage />
              </LazyPage>
            ),
          },
          {
            path: 'deals/new',
            element: (
              <LazyPage>
                <DealFormPage />
              </LazyPage>
            ),
          },
          {
            path: 'deals/:id',
            element: (
              <LazyPage>
                <DealDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'profile',
            element: (
              <LazyPage>
                <ProfilePage />
              </LazyPage>
            ),
          },
          {
            path: 'mfa-setup',
            element: (
              <LazyPage>
                <MfaSetupPage />
              </LazyPage>
            ),
          },
          {
            path: 'commissions',
            element: (
              <LazyPage>
                <CommissionsPage />
              </LazyPage>
            ),
          },
          {
            path: 'commissions/:id',
            element: (
              <LazyPage>
                <CommissionDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'team',
            element: (
              <LazyPage>
                <TeamPage />
              </LazyPage>
            ),
          },
          {
            path: 'resources',
            element: (
              <LazyPage>
                <ResourcesPage />
              </LazyPage>
            ),
          },
          {
            path: 'tiers',
            element: (
              <LazyPage>
                <TiersPage />
              </LazyPage>
            ),
          },
        ],
      },
      // Admin routes
      {
        element: <AdminRoute />,
        children: [
          {
            path: 'admin',
            element: <Navigate to="/admin/dashboard" replace />,
          },
          {
            path: 'admin/dashboard',
            element: (
              <LazyPage>
                <AdminDashboardPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/deals',
            element: (
              <LazyPage>
                <AdminDealsPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/deals/:id',
            element: (
              <LazyPage>
                <AdminDealDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/commissions',
            element: (
              <LazyPage>
                <AdminCommissionsPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/commissions/:id',
            element: (
              <LazyPage>
                <CommissionDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/organizations',
            element: (
              <LazyPage>
                <AdminOrganizationsPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/organizations/:id',
            element: (
              <LazyPage>
                <AdminOrganizationDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/users',
            element: (
              <LazyPage>
                <AdminUsersPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/users/:id',
            element: (
              <LazyPage>
                <AdminUserDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/customers',
            element: (
              <LazyPage>
                <AdminCustomersPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/customers/:id',
            element: (
              <LazyPage>
                <AdminCustomerDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/audit-log',
            element: (
              <LazyPage>
                <AdminAuditLogPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/reports',
            element: (
              <LazyPage>
                <AdminReportsPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/offerings',
            element: (
              <LazyPage>
                <AdminOfferingsPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/offerings/:id',
            element: (
              <LazyPage>
                <AdminOfferingDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/scoping-questions',
            element: (
              <LazyPage>
                <AdminScopingQuestionsPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/settings',
            element: (
              <LazyPage>
                <AdminSettingsPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/join-requests',
            element: <Navigate to="/admin/organizations?type=pending_approvals" replace />,
          },
          {
            path: 'admin/resources',
            element: (
              <LazyPage>
                <AdminResourcesPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/resource-analytics',
            element: (
              <LazyPage>
                <AdminResourceAnalyticsPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/settings/integrations/zoho',
            element: (
              <LazyPage>
                <ZohoTestingPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/settings/integrations/quickbooks',
            element: (
              <LazyPage>
                <QuickBooksTestingPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/settings/integrations/rabbitsign',
            element: (
              <LazyPage>
                <RabbitSignTestingPage />
              </LazyPage>
            ),
          },
          {
            path: 'admin/settings/integrations/gmail',
            element: (
              <LazyPage>
                <GmailTestingPage />
              </LazyPage>
            ),
          },
        ],
      },
    ],
  },
]);
